import { useEffect, type RefObject } from "react";
import { examplePostEngagement } from "../../../shared/src/example-post-comments";
import { postCountLabel } from "../../../shared/src/post-likes-runtime";

const icon = {
  width: 20,
  height: 20,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.75,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
} as const;

export type PostActionsPost = { id: string; title: string; locked: boolean; example?: true };

/**
 * A post's likes, comments, sharing and gifting: the row that closes a social card and a post's
 * own page. Counts and the reader's heart come from a private answer after the page loads
 * (site/src/ui/post-actions.ts), so they start empty; an example carries fictional ones. Gift
 * stays hidden until that answer says the site offers gift memberships.
 */
export function PostActions({
  post,
  href,
  commentsHref,
  giftHref,
  editHref,
}: {
  post: PostActionsPost;
  /** The post's own address, which Share hands on; none leaves Share out. */
  href: string | null;
  commentsHref: string;
  giftHref: string;
  editHref?: string;
}) {
  const example = examplePostEngagement(post);
  return (
    <footer className="drate-post-actions">
      <div className="drate-post-actions-group">
        <button type="button" className="drate-post-action" data-post-like="" aria-pressed="false">
          <svg {...icon}>
            <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
          </svg>
          <span className="sr-only">Like</span>
          <span className="drate-post-count" data-post-likes="">
            {example ? postCountLabel(example.likes) : ""}
          </span>
        </button>
        <a className="drate-post-action" href={commentsHref}>
          <svg {...icon}>
            <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
          </svg>
          <span className="sr-only">Comments</span>
          <span className="drate-post-count" data-post-comments="">
            {example ? postCountLabel(example.comments) : ""}
          </span>
        </a>
      </div>
      <div className="drate-post-actions-group">
        {href && (
          <button
            type="button"
            className="drate-post-action"
            data-post-share=""
            data-url={href}
            data-title={post.title}
          >
            <svg {...icon} className="drate-post-share-icon">
              <path d="M12 3v12M8 7l4-4 4 4M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
            </svg>
            <svg {...icon} className="drate-post-copied-icon">
              <path d="m5 12.5 4.5 4.5L19 7.5" />
            </svg>
            <span className="drate-post-action-label" data-post-share-label="">
              Share
            </span>
          </button>
        )}
        <a className="drate-post-action" href={giftHref} data-post-gift="" hidden={!post.example}>
          <svg {...icon}>
            <path d="M4 12h16v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-7ZM3 8h18v4H3zM12 8v13M12 8S10.5 3 7.5 3a2.5 2.5 0 0 0 0 5M12 8s1.5-5 4.5-5a2.5 2.5 0 0 1 0 5" />
          </svg>
          <span className="drate-post-action-label">Gift</span>
        </a>
        {post.locked && (
          <span className="drate-post-action" data-post-locked="">
            <svg {...icon} strokeWidth={2}>
              <rect x="4.5" y="10.5" width="15" height="11" rx="2.5" fill="currentColor" />
              <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
            </svg>
            <span className="drate-post-action-label">Locked</span>
          </span>
        )}
        {editHref && (
          <a className="drate-post-action" href={editHref} data-reload="">
            Edit
          </a>
        )}
      </div>
    </footer>
  );
}

/**
 * Marks the element a feed's or a post page's actions live in: where signing in and joining
 * lead (a card's own `data-join` wins), and its status line. Spread it on the element and render
 * `PostActionsStatus` inside.
 */
export function postActionsScope(signInHref: string, joinHref: string) {
  return { "data-ui": "post-actions", "data-sign-in": signInHref, "data-join": joinHref };
}

/** Announces a copied link or a like that didn't save; heard, not seen. */
export function PostActionsStatus() {
  return <p className="sr-only" role="status" data-post-status="" />;
}

/** Loads the actions script once the scope is on the page, and again when its posts change. */
export function usePostActions(
  scope: RefObject<HTMLElement | null>,
  enabled: boolean,
  posts: string,
) {
  useEffect(() => {
    const element = scope.current;
    if (!enabled || !element) return;
    let cancelled = false;
    void import("../../src/ui/post-actions").then(({ mountPostActions }) => {
      if (!cancelled && scope.current === element) mountPostActions(element);
    });
    return () => {
      cancelled = true;
    };
  }, [scope, enabled, posts]);
}
